/**
 * API Helper with debouncing and caching features
 * This utility helps prevent duplicate requests and improve performance
 * 
 * USAGE GUIDE:
 * 
 * 1. Import the utility:
 *    import { debouncedRequest } from '../utils/apiHelper';
 * 
 * 2. Use it for API calls:
 *    const data = await debouncedRequest(cacheKey, async () => {
 *      const response = await apiClient.get('/api/endpoint');
 *      return response.data;
 *    });
 * 
 * 3. Create specific cache keys for each unique request:
 *    const cacheKey = `manifest-${manifestName}-${query}`;
 *    
 * BENEFITS:
 * - Prevents duplicate identical API calls
 * - Caches responses for faster repeat access
 * - Automatically handles pending requests
 * - Works with any async request function
 */

// In-memory request cache
const requestCache = new Map();
const pendingRequests = new Map();

// Default TTL for cache entries (5 minutes)
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

/**
 * Execute a request with debouncing and caching
 * 
 * @param {string} key - Unique key for this request
 * @param {Function} requestFn - Function that returns a promise for the request
 * @param {Object} options - Cache options
 * @param {number} options.ttl - Time to live in ms for cache entries (default: 5 minutes)
 * @param {boolean} options.force - Whether to bypass cache and force a new request
 * @returns {Promise} - The request promise
 */
export const debouncedRequest = (key, requestFn, options = {}) => {
  const { ttl = DEFAULT_CACHE_TTL, force = false } = options;
  
  // Check cache if we're not forcing a refresh
  if (!force) {
    const cachedItem = requestCache.get(key);
    if (cachedItem && cachedItem.expires > Date.now()) {
      console.log(`[ApiHelper] Using cached data for: ${key}`);
      return Promise.resolve(cachedItem.data);
    }
  }
  
  // Check for pending requests with the same key
  if (pendingRequests.has(key)) {
    console.log(`[ApiHelper] Reusing in-flight request for: ${key}`);
    return pendingRequests.get(key);
  }
  
  // Execute the request
  console.log(`[ApiHelper] Making fresh request for: ${key}`);
  const promise = Promise.resolve().then(requestFn)
    .then(result => {
      // Cache successful result
      requestCache.set(key, {
        data: result,
        expires: Date.now() + ttl
      });
      
      // Remove from pending requests
      pendingRequests.delete(key);
      return result;
    })
    .catch(error => {
      // Remove from pending requests on error
      pendingRequests.delete(key);
      throw error;
    });
  
  // Store the pending request
  pendingRequests.set(key, promise);
  
  return promise;
};

/**
 * Clear the request cache
 * 
 * @param {string} [key] - Optional specific key to clear (clears all if omitted)
 */
export const clearRequestCache = (key) => {
  if (key) {
    requestCache.delete(key);
  } else {
    requestCache.clear();
  }
};

/**
 * Get stats about the request cache
 * 
 * @returns {Object} Cache statistics
 */
export const getRequestCacheStats = () => {
  return {
    cacheSize: requestCache.size,
    pendingCount: pendingRequests.size,
    cacheKeys: Array.from(requestCache.keys())
  };
};

export default {
  debouncedRequest,
  clearRequestCache,
  getRequestCacheStats
};